import { useSeoMeta } from '@unhead/react';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const Index = () => {
  useSeoMeta({
    title: 'Test Page',
    description: 'A test page for the Shakespeare project template.',
  });

  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey);
  const metadata = author.data?.metadata;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
              <span className="text-white text-sm font-bold">T</span>
            </div>
            <span className="font-semibold text-slate-800 dark:text-slate-100">Test Page</span>
          </div>
          <LoginArea className="max-w-48" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12 space-y-8">

        {/* Hero */}
        <div className="text-center space-y-4">
          <Badge variant="secondary" className="text-xs">MKStack Template</Badge>
          <h1 className="text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            Your Test Page
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
            This is a test page built with React, TailwindCSS, and Nostrify. Log in with Nostr to see your profile info below.
          </p>
        </div>

        <Separator />

        {/* Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="font-semibold text-slate-800 dark:text-slate-100">Online</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Framework</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="font-semibold text-slate-800 dark:text-slate-100">React 19</span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Protocol</CardTitle>
            </CardHeader>
            <CardContent>
              <span className="font-semibold text-slate-800 dark:text-slate-100">Nostr</span>
            </CardContent>
          </Card>
        </div>

        {/* User Info */}
        <Card>
          <CardHeader>
            <CardTitle>Nostr Identity</CardTitle>
          </CardHeader>
          <CardContent>
            {user ? (
              <div className="flex items-center gap-4">
                {metadata?.picture ? (
                  <img
                    src={metadata.picture}
                    alt={metadata.name ?? 'Profile'}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-violet-200 dark:ring-violet-800"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-xl font-bold">
                    {(metadata?.name ?? 'A')[0].toUpperCase()}
                  </div>
                )}
                <div className="space-y-1">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-lg">
                    {metadata?.display_name ?? metadata?.name ?? 'Anonymous'}
                  </p>
                  {metadata?.about && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm line-clamp-2">
                      {metadata.about}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">
                    {user.pubkey.slice(0, 16)}…
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 space-y-3">
                <p className="text-slate-500 dark:text-slate-400">
                  Log in with your Nostr account to see your profile here.
                </p>
                <LoginArea className="max-w-48 mx-auto" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-400 dark:text-slate-600 pt-4">
          Vibed with{' '}
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-violet-500 transition-colors"
          >
            Shakespeare
          </a>
        </div>
      </main>
    </div>
  );
};

export default Index;
